import { useTheme, Box, Button, Card, CardContent, Container, Grid, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import SolarPowerRoundedIcon from "@mui/icons-material/SolarPowerRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";

export default function UeberUns() {
  const { t } = useTranslation();
  const theme = useTheme();

  const valuesItems = t("aboutPage.values.items", { returnObjects: true }) as { title: string; description: string }[];
  const missionItems = t("aboutPage.mission.items", { returnObjects: true }) as { title: string }[];

  const missionIcons = [
    <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 60 }} />,
    <HandshakeRoundedIcon sx={{ fontSize: 60 }} />,
    <SolarPowerRoundedIcon sx={{ fontSize: 60 }} />,
    <StarOutlineRoundedIcon sx={{ fontSize: 60 }} />,
  ];

  return (
    <Box sx={{ py: { xs: 5, md: 8 } }} id="ueber-uns">
      <Container maxWidth="lg">
        {/* Intro Section */}
        <Stack spacing={4} sx={{ mb: 8, maxWidth: 800 }}>
          <Box>
            <Box sx={{ mb: 4 }}>
              <img src={theme.palette.mode === "dark" ? "/logo_transparent_darkmode.png" : "/logo_transparent.png"} alt="RSS-Energie Logo" style={{ height: 60, width: "auto", objectFit: "contain" }} />
            </Box>
            <Typography variant="h1" sx={{ mb: 1.5 }}>
              {t("aboutPage.hero.title")}
            </Typography>
            <Typography variant="h4" color="secondary.main" fontWeight={600}>
              {t("aboutPage.hero.subtitle")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body1" sx={{ mb: 2, fontSize: "1.1rem", lineHeight: 1.7 }}>
              {t("aboutPage.intro.p1")}
            </Typography>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: "1.1rem" }}>
              {t("aboutPage.intro.p2")}
            </Typography>
          </Box>
        </Stack>

        {/* Versprechen Section */}
        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, backgroundColor: "var(--surface)", mb: 8, borderRadius: 4 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("aboutPage.promise.title")}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1.1rem", maxWidth: 900, lineHeight: 1.7 }}>
            {t("aboutPage.promise.body")}
          </Typography>
        </Paper>

        {/* Scenic Van Image */}
        <Box sx={{ mb: 10, borderRadius: 4, overflow: "hidden" }}>
          <img
            src="/assets/van_rear_left_view_1772341008495.png"
            alt="RSS-Energie Firmenwagen unterwegs"
            style={{ width: "100%", height: "auto", display: "block", maxHeight: "600px", objectFit: "cover" }}
          />
        </Box>

        {/* Werte & Philosophie Section */}
        <Box sx={{ mb: 10 }}>
          <Stack spacing={2} sx={{ mb: 5, maxWidth: 800 }}>
            <Typography variant="h2">{t("aboutPage.values.title")}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem" }}>
              {t("aboutPage.values.intro")}
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {valuesItems.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Card elevation={0} sx={{ height: "100%", border: "1px solid var(--border)", backgroundColor: "var(--surface)" }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" sx={{ mb: 2, color: "secondary.main" }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Mission Section (Screenshot UI) */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h2" align="center" sx={{ mb: 5 }}>
            {t("aboutPage.mission.title")}
          </Typography>
          <Paper elevation={2} sx={{ borderRadius: 0, overflow: "hidden" }}>
            <Grid container>
              {missionItems.map((item, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{
                    borderRight: { xs: "none", md: index < 3 ? "2px solid #e0e0e0" : "none" },
                    borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
                  }}
                >
                  <Box
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                      color: "#8fc43f", // Match the green icon style
                    }}
                  >
                    <Box sx={{ mb: 3 }}>{missionIcons[index]}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "text.primary",
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>

        {/* Conclusion & CTA Section */}
        <Box sx={{ textAlign: "center", maxWidth: 800, mx: "auto" }}>
          <Stack spacing={3} sx={{ mb: 5 }}>
            <Typography variant="body1" fontWeight={700} sx={{ fontSize: "1.2rem", color: "secondary.main" }}>
              {t("aboutPage.conclusion.p1")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem", lineHeight: 1.7 }}>
              {t("aboutPage.conclusion.p2")}
            </Typography>
          </Stack>
          <Button component={RouterLink} to="/kontakt" variant="contained" color="secondary" size="large" sx={{ px: 6, py: 1.5 }}>
            {t("aboutPage.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
